import { useEffect, useRef, useState } from "react";
import { animate, motion, useInView, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { CHAPTERS, COMPANY, IMAGES, STATS } from "@/data";

function Counter({ value, suffix, decimals = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [display, setDisplay] = useState("0");
  const reduce = useReducedMotion();

  useEffect(() => {
    if (!inView) return;
    if (reduce) {
      setDisplay(value.toLocaleString("de-DE", { maximumFractionDigits: decimals, minimumFractionDigits: decimals }));
      return;
    }
    const controls = animate(0, value, {
      duration: 1.8,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) =>
        setDisplay(v.toLocaleString("de-DE", { maximumFractionDigits: decimals, minimumFractionDigits: decimals })),
    });
    return () => controls.stop();
  }, [inView, value, decimals, reduce]);

  return (
    <span ref={ref}>
      {display}
      {suffix}
    </span>
  );
}

export default function About() {
  const imgRef = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <section id="ueber-uns" data-testid="about-section" className="border-y border-slate-200 bg-white py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-start gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <span className="inline-block rounded-full border border-teal-200/70 bg-teal-50/80 px-3 py-1 font-mono text-xs uppercase tracking-[0.25em] text-teal-800">
              Über uns
            </span>
            <h2 className="mt-4 font-serif text-3xl font-medium tracking-tight text-slate-900 sm:text-4xl">
              Vier Versprechen.<br />
              <em className="italic text-sky-700">Ein Ansprechpartner.</em>
            </h2>

            <div ref={imgRef} className="relative mt-10 overflow-hidden rounded-2xl">
              <div className="aspect-[4/3] overflow-hidden">
                <motion.img
                  src={IMAGES.action}
                  alt="Fensterreiniger bei der Arbeit"
                  style={reduce ? undefined : { y }}
                  className="h-[112%] w-full scale-105 object-cover"
                />
              </div>
              <div className="absolute bottom-4 left-4 rounded-lg border border-white/30 bg-white/85 px-4 py-2.5 backdrop-blur-md">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Inhabergeführt</p>
                <p className="font-serif text-sm font-medium text-slate-900">
                  Inhaber {COMPANY.owner} · {COMPANY.founded}
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="divide-y divide-slate-100 border-y border-slate-100">
              {CHAPTERS.map((c, i) => (
                <motion.div
                  key={c.nr}
                  data-testid={`manifesto-chapter-${c.nr}`}
                  initial={{ opacity: 0, y: reduce ? 0 : 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                  className="grid grid-cols-[64px_1fr] gap-6 py-8"
                >
                  <span className="font-mono text-sm font-semibold text-sky-700">{c.nr}</span>
                  <div>
                    <h3 className="font-serif text-xl font-medium text-slate-900 sm:text-2xl">{c.title}</h3>
                    <p className="mt-2 max-w-xl text-sm leading-relaxed text-slate-600 sm:text-base">{c.text}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <div data-testid="about-stats" className="mt-12 grid grid-cols-2 gap-8 sm:grid-cols-4">
              {STATS.map((s) => (
                <div key={s.label}>
                  <p className="font-serif text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
                    <Counter value={s.value} suffix={s.suffix} decimals={s.decimals || 0} />
                  </p>
                  <p className="mt-1 text-xs font-medium uppercase tracking-wider text-slate-500">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
